/**
 * Shared copy and API error formatting for end users (no dev jargon).
 */

const FIELD_LABELS = {
  phone_number: "Phone number",
  company_name: "Company name",
  first_name: "First name",
  last_name: "Last name",
  email: "Email",
  role: "Job title",
  username: "Email",
  password: "Password",
  old_password: "Current password",
  new_password1: "New password",
  new_password2: "Confirm new password",
  module: "Module",
  message: "Message",
  auth_user: "Account",
  request_type: "Request type",
  is_admin: "Administrator",
  is_developer: "Developer",
  is_active: "Status",
  module_ids: "Modules",
};

function labelForField(key) {
  if (!key || key === "detail") {
    return null;
  }
  if (FIELD_LABELS[key]) {
    return FIELD_LABELS[key];
  }
  return String(key)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Turn DRF validation payloads into a single readable sentence for customers.
 */
export function flattenDjangoRestError(data) {
  if (data == null) {
    return null;
  }
  if (typeof data === "string") {
    const t = data.trim();
    if (t.startsWith("<!") || t.startsWith("<html")) {
      return null;
    }
    return t;
  }
  if (typeof data !== "object") {
    return String(data);
  }
  if (data.detail !== undefined) {
    const d = data.detail;
    if (Array.isArray(d)) {
      return d.map(String).join(" ");
    }
    return String(d);
  }
  if (Array.isArray(data.non_field_errors)) {
    return data.non_field_errors.map(String).join(" ");
  }
  const parts = [];
  for (const [key, val] of Object.entries(data)) {
    if (val == null || key === "detail") {
      continue;
    }
    const text = Array.isArray(val) ? val.map(String).join(", ") : String(val);
    if (!text) {
      continue;
    }
    if (key === "non_field_errors") {
      parts.push(text);
      continue;
    }
    const label = labelForField(key);
    parts.push(label ? `${label}: ${text}` : text);
  }
  return parts.length ? parts.join(" ") : null;
}

export const copy = {
  profileLoadSession: "Your session has expired. Please sign in again.",
  profileLoadFailed: "We couldn't load your profile. Please try again.",
  profileStillLoading: "Your profile is still loading. Wait a moment or refresh the page.",
  profilePhoneRequired: "Please add a phone number.",
  profileSaved: "Your profile was saved.",
  profileSaveFailedGeneric: "We couldn't save your profile. Please try again.",
  profileSaveServer:
    "Something went wrong on our side. Please try again in a few minutes. If this keeps happening, contact support.",
  profileSaveNetwork:
    "We couldn't reach the service. Check your internet connection and try again.",
  profileSaveFallback: "We couldn't save your changes. Please try again.",

  contactSent: "Thanks — your message was sent. We'll get back to you soon.",
  contactRequestSent: "Thanks — your request was sent.",
  contactServer:
    "Something went wrong on our side. Please try again in a few minutes.",
  contactNetwork: "We couldn't reach the service. Check your connection and try again.",
  contactFailed: "We couldn't send your message. Please try again.",

  passwordUpdated: "Your password was updated.",
  passwordChangeFailed: "We couldn't update your password. Please try again.",
  passwordChangeWrongCurrent:
    "That didn't work. Check your current password and try again.",
  passwordChangeCurrentRequired: "Please enter your current password.",

  resetEmailSent:
    "If an account exists for that email, we've sent a link to reset your password.",
  resetEmailFailed: "We couldn't send the email. Check the address and try again.",
  resetEmailRequired: "Please enter your email address.",
  resetEmailThrottled: "Too many requests. Please try again later.",
  resetLinkInvalid: "This link is invalid or has expired. Request a new one.",
  resetPasswordFailed: "We couldn't set your password. Please try again.",

  loginFailed:
    "We couldn't sign you in. Check your email and password and try again.",
};
